import { Group } from "@dashboard-v3/api";
import { MapCSVRow, parseContent } from "utils/csv";
import isEmail from "utils/isEmail";
import { bytesToUnit, Size, toSize } from "utils/formatBytes";
import { ActionType } from "../../reducer";
import { LoadedAccount, LoadedFile } from "../../types";
import { useTranslation } from "react-i18next";
import { fetchGroup, fetchGroups } from "utils/api/groups";

const sizeLimit = toSize(100, "MB");
const maxFiles = 20;

export default function useCsvAccountParser(action?: ActionType) {
  const { t } = useTranslation("accounts");

  function validateFiles(files: File[]) {
    if (someIsTooBig(files, sizeLimit)) {
      return "importAccountsModal.import.fileSizeError";
    }

    if (files.length > maxFiles) {
      return "importAccountsModal.import.fileCountError";
    }

    return false;
  }

  async function parseFiles(files: File[]): Promise<LoadedFile[]> {
    return await Promise.all(
      files.map(async file => {
        const content = await file.text();
        const result = await parseContent<LoadedAccount>(
          content,
          action === "DELETE" ? mapDeleteCsvRow : mapCsvRow,
          t
        );
        return { fileName: file.name, ...result };
      })
    );
  }

  return { validateFiles, parseFiles };
}

const mapDeleteCsvRow: MapCSVRow<LoadedAccount> = async (values, error, t) => {
  const email = (values[0] || "").trim();

  if (!email || !isEmail(email)) {
    return error(t("importAccounts.row.invalidEmail"), 1);
  }

  return {
    account: { email, aliases: [], tags: [] },
    approvalGroups: [],
  };
};

const mapCsvRow: MapCSVRow<LoadedAccount> = async (values, error, t) => {
  /* 
  # Positions:
  0: email
  1: aliases
  2: boxAccount
  3: onedriveAccount
  4: driveAccount
  5: dropboxAccount
  6: tags
  7: approvalGroups
  */

  if (values.length !== 8) {
    return error(
      t("importAccounts.missingColumn", {
        expectedColumns: 7,
        foundColumns: values.length,
      })
    );
  }

  const email = (values[0] || "").trim() as string;

  if (!email || !isEmail(email)) {
    error(t("importAccounts.row.invalidEmail"), 1);
  }

  const aliases = stringToArray(values[1] || "");
  if (aliases) {
    const wrongAliases = aliases.filter(
      alias => alias && !isEmail(alias.trim())
    );

    if (wrongAliases.length > 0) {
      const errorMsg = t(`importAccounts.row.invalidAliases`, {
        count: wrongAliases.length,
        wrongEmails: wrongAliases.join(", "),
      });

      error(errorMsg, 2);
    }
  }

  const boxAccount = (values[2] || "").trim() as string;
  if (boxAccount && !isEmail(boxAccount)) {
    error(t("importAccountsModal.files.error.invalidBoxAccount"), 3);
  }

  const onedriveAccount = (values[3] || "").trim() as string;
  if (onedriveAccount && !isEmail(onedriveAccount)) {
    error(t("importAccountsModal.files.error.invalidOnedriveAccount"), 4);
  }

  const driveAccount = (values[4] || "").trim() as string;
  if (driveAccount && !isEmail(driveAccount)) {
    error(t("importAccountsModal.files.error.invalidDriveAccount"), 5);
  }

  const dropboxAccount = (values[5] || "").trim() as string;
  if (dropboxAccount && !isEmail(dropboxAccount)) {
    error(t("importAccountsModal.files.error.invalidDropboxAccount"), 6);
  }

  const tags = stringToArray(values[6] || "");
  const rawApprovalGroups = (values[7] || "").trim();
  const approvalGroups = parseApprovalGroups(rawApprovalGroups);

  let groups: Group[] = [];
  let groupNotFound = false;
  if (approvalGroups.length) {
    // TODO: apply cache in fetchGroups and fetchGroup
    groups = await Promise.all(
      approvalGroups.map(async groupIdOrName => {
        if (groupNotFound) return;

        const [groupByName, groupById] = await Promise.all([
          fetchGroups(1, 0, groupIdOrName),
          fetchGroup(groupIdOrName).catch(error => null),
        ]);

        const group = groupById ?? groupByName?.[0];

        if (!group) {
          groupNotFound = true;
          error(
            t("importAccounts.row.approvalGroupNotFound", {
              group: groupIdOrName,
            }),
            7
          );
        }

        return group;
      })
    );
  }

  if (error.some()) return;

  const item: LoadedAccount = {
    account: {
      email,
      aliases: aliases || [],
      storage: {
        box: boxAccount || null,
        onedrive: onedriveAccount || null,
        drive: driveAccount || null,
        dropbox: dropboxAccount || null,
      },
      approvalGroups: groups.map(group => group.id),
      tags,
    },
    approvalGroups: groups,
  };

  return item;
};

function someIsTooBig(files: File[], limitSize: Size) {
  return files.some(f => bytesToUnit(f.size, limitSize.unit) > limitSize.value);
}

function stringToArray(str: string) {
  const string = str?.replace(/(\r\n|\n|\r)/gm, "");
  return string ? string.split(" ") : [];
}

/**
 * @example
 * '"Non-admin Nachi Group" "Test long email group"' -> ["Non-admin Nachi Group", "Test long email group"]
 * 'Testing group' -> ['Testing group']
 * '"Group with spaces"' -> ['Group with spaces']
 * 'Group1 Group2 Group3' -> ['Group1 Group2 Group3']
 * 'SimpleGroup "Group with spaces" AnotherGroup' -> ['SimpleGroup', 'Group with spaces', 'AnotherGroup']
 */
export function parseApprovalGroups(str: string): string[] {
  if (!str || !str.trim()) {
    return [];
  }

  const trimmed = str.trim();

  // Check if the string contains any quotes
  if (!trimmed.includes('"')) {
    // No quotes, treat as single group
    return [trimmed];
  }

  const groups: string[] = [];
  let current = "";
  let inQuotes = false;
  let i = 0;

  while (i < trimmed.length) {
    const char = trimmed[i];

    if (char === '"') {
      if (inQuotes) {
        // Closing quote - add the current group
        if (current.trim()) {
          groups.push(current.trim());
        }
        current = "";
        inQuotes = false;
      } else {
        // Opening quote - if we have unquoted content before, add it
        if (current.trim()) {
          // Split unquoted content by spaces
          const unquotedGroups = current
            .trim()
            .split(/\s+/)
            .filter(g => g.length > 0);
          groups.push(...unquotedGroups);
          current = "";
        }
        inQuotes = true;
      }
    } else if (char === " " && !inQuotes) {
      // Skip spaces outside quotes, they separate groups
      if (current.trim()) {
        current += " ";
      }
    } else {
      // Regular character
      current += char;
    }

    i++;
  }

  // Add any remaining unquoted content
  if (current.trim()) {
    const unquotedGroups = current
      .trim()
      .split(/\s+/)
      .filter(g => g.length > 0);
    groups.push(...unquotedGroups);
  }

  return groups;
}
